import {
  ComponentPropsWithoutRef,
  ComponentRef,
  ComponentType,
  ElementType,
  ForwardedRef,
  forwardRef,
  ReactElement,
  RefAttributes,
} from 'react'

import { useButtonClickVerification } from '@sparkai/hooks'
import { cn, isAutomatedBrowser } from '@sparkai/utils'

export const AvailableButtonSizes = ['xs', 'sm', 'base', 'lg', 'xl', 'default'] as const
export type ButtonSize = (typeof AvailableButtonSizes)[number]

export const AvailableButtonVariants = [
  'primary',
  'primary-lighter',
  'white',
  'red',
  'text',
  'unstyled',
] as const
export type ButtonVariant = (typeof AvailableButtonVariants)[number]

export interface ButtonBaseProps {
  size?: ButtonSize
  isLoading?: boolean
  variant?: ButtonVariant
  icon?: ComponentType<ComponentPropsWithoutRef<'svg'>>
  rightIcon?: ComponentType<ComponentPropsWithoutRef<'svg'>>
  clickVerification?: boolean
  tabIndex?: number
}

export type ButtonProps<TElement extends ElementType> = ComponentPropsWithoutRef<TElement> &
  ButtonBaseProps & {
    as?: TElement
  }

const ButtonComponent = <TElement extends ElementType>(
  {
    as,
    size = 'base',
    variant = 'primary',
    children,
    className: classNameFromProps,
    icon,
    rightIcon,
    isLoading,
    clickVerification,
    tabIndex,
    ...props
  }: ButtonProps<TElement>,
  ref: ForwardedRef<ComponentRef<TElement>>
) => {
  const Element = as ?? 'button'
  const Icon = icon
  const RightIcon = rightIcon

  const { handleClick } = useButtonClickVerification()

  return (
    <Element
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as any}
      type='button'
      className={cn(
        className.base,
        className.sizes[size],
        className.variants[variant],
        {
          'shadow-xs focus:ring-2 focus:ring-offset-2 focus:outline-hidden': !['text', 'unstyled'].includes(
            variant
          ),
        },
        classNameFromProps
      )}
      tabIndex={tabIndex}
      {...props}
      onClick={(event) => {
        if (isAutomatedBrowser || !clickVerification) {
          props?.onClick?.(event)
          return
        }

        props?.onClick && handleClick(event, props.onClick)
      }}
    >
      {isLoading && (
        <svg
          className='mr-2 h-5 w-5 animate-spin'
          xmlns='http://www.w3.org/2000/svg'
          fill='none'
          viewBox='0 0 24 24'
        >
          <circle
            className='opacity-25'
            cx='12'
            cy='12'
            r='10'
            stroke='currentColor'
            strokeWidth='4'
          ></circle>
          <path
            className='opacity-75'
            fill='currentColor'
            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
          ></path>
        </svg>
      )}

      {Icon && size && <Icon className={className.sizesIcon[size]} aria-hidden='true' />}
      {children}
      {RightIcon && <RightIcon className={className.sizesIconRight[size]} aria-hidden='true' />}
    </Element>
  )
}

// Using type cast to make it possible to have generics even when using forwardRef
export const Button = forwardRef(ButtonComponent) as <TElement extends ElementType = 'button'>(
  p: ButtonProps<TElement> & RefAttributes<ComponentRef<TElement>>
) => ReactElement

const className = {
  base: 'inline-flex items-center border font-medium  disabled:cursor-not-allowed font-inter',
  sizes: {
    xs: 'px-2.5 py-1.5 text-xs rounded',
    sm: 'px-3 py-2 text-sm leading-4 rounded-md',
    base: 'px-4 py-2 text-sm rounded-md',
    lg: 'px-4 py-2 text-base rounded-md',
    xl: 'px-6 py-3 text-base rounded-md',
    default: 'px-2 py-2 rounded-md',
  },
  sizesIcon: {
    xs: '-ml-0.5 mr-2 h-3 w-3',
    sm: '-ml-0.5 mr-2 h-4 w-4',
    base: '-ml-1 mr-2 h-5 w-5',
    lg: '-ml-1 mr-3 h-5 w-5',
    xl: '-ml-1 mr-3 h-5 w-5',
    default: '',
  },
  sizesIconRight: {
    xs: '-mr-0.5 ml-2 h-3 w-3',
    sm: '-mr-0.5 ml-2 h-4 w-4',
    base: '-mr-1 ml-2 h-5 w-5',
    lg: '-mr-1 ml-3 h-5 w-5',
    xl: '-mr-1 ml-3 h-5 w-5',
    default: '',
  },
  variants: {
    red: 'bg-[#DC2626] hover:bg-[#B91C1C] focus:ring-[#B91C1C] disabled:bg-[#FCA5A5] text-white border-transparent',
    primary:
      'bg-blue-600 border-transparent text-white hover:bg-blue-700  focus:ring-blue-500 disabled:bg-blue-300',
    ['primary-lighter']:
      'bg-blue-500 border-transparent text-white hover:bg-blue-600  focus:ring-blue-400 disabled:bg-blue-200',
    white: 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-blue-500  disabled:bg-gray-50',
    text: 'border-transparent text-gray-600 disabled:text-gray-300',
    unstyled: 'border-transparent sh',
  },
}
